import getLogger, {type Logger} from "../logging.ts";
import {
    type LicensePayload,
    type LicenseEvaluationResult,
    type LicenseValidatorOptions,
    licenseValidationResults, type LicenseValidationOptions
} from "./types.ts";
import {KJUR} from 'jsrsasign';

export class LicenseError extends Error {
    public readonly licensePayload: LicensePayload | string;

    constructor(message: string, licensePayload: string | LicensePayload) {
        super(message);
        this.licensePayload = licensePayload;
    }
}

export class LicenseEvaluator {
    private readonly tokenResultMap = {
        'trial-false': licenseValidationResults.TRIAL_LICENSE_VALID,
        'trial-true': licenseValidationResults.TRIAL_LICENSE_EXPIRED,
        'paid-false': licenseValidationResults.PAID_LICENSE_VALID,
        'paid-true': licenseValidationResults.PAID_LICENSE_EXPIRED,
    };

    private readonly validationKey: string;

    constructor(validationKey: string) {
        if (!validationKey || typeof validationKey !== 'string') {
            throw new Error(`Validation key must be a non-empty string`);
        }
        this.validationKey = validationKey;
    }

    private decrypt(token: string): object {
        if (KJUR.jws.JWS.verifyJWT(token, this.validationKey, {alg: ['RS256']})) {
            return KJUR.jws.JWS.parse(token).payloadObj;
        } else {
            throw new LicenseError('invalid jwt token', token);
        }
    }

    private validateBasicTokenData(token: string): LicensePayload {
        const decoded = this.decrypt(token) as LicensePayload;
        if (['paid', 'trial'].indexOf(decoded.type) === -1) {
            throw new LicenseError('Invalid license type', token);
        }
        return decoded;
    }

    getLicenseStatus(token: string, now: Date = new Date()): LicenseEvaluationResult {
        try {
            const license = this.validateBasicTokenData(token);
            const isExpired = license.expiration < now.getTime();
            const licenseValidationResult = this.tokenResultMap[`${license.type}-${isExpired}`];
            return {
                ...licenseValidationResult,
                license
            };
        } catch (e) {
            return {
                ...licenseValidationResults.NO_LICENSE,
                error: e.message,
            };
        }

    }
}

export class LicenseValidator {
    private readonly licenseHelper: LicenseEvaluator;
    private readonly logger: Logger;

    constructor({validationKey, logger}: LicenseValidatorOptions) {
        this.licenseHelper = new LicenseEvaluator(validationKey);
        this.logger = logger ?? getLogger("LicenseValidator"); // Default to console if no logger provided
    }

    public validate(licenseKeyString: string, options?: LicenseValidationOptions): void {
        if (typeof licenseKeyString !== 'string') {
            throw new LicenseError('Invalid license key format', licenseKeyString);
        }

        const licenseStatus = this.licenseHelper.getLicenseStatus(licenseKeyString);
        this.printLicenseMessage(licenseStatus, options?.logSuccessMessages ?? true);
        if (licenseStatus.fatal) {
            throw new LicenseError('License validation failed', licenseStatus.license);
        }
    }

    private printLicenseMessage(licenseStatus: LicenseEvaluationResult, logSuccessMessages: boolean): void {
        if (!licenseStatus || licenseStatus.licenseType === 'none') {
            this.logger.error('This license is invalid. Please contact us at sales@interop.io to get a valid license');
            return;
        }

        const message = [
            licenseStatus.licenseType === 'paid' ? `This is a paid license that was issued for client ` : `This is a trial license that was issued for `,
            licenseStatus.license.organization.displayName,
            licenseStatus.expired ? `, but it has expired at ` : ` and is valid until `,
            new Date(licenseStatus.license.expiration).toString(),
            '.'
        ].join('');

        if (licenseStatus.fatal) {
            this.logger.error(message);
        } else if (licenseStatus.expired) {
            this.logger.warn(message);
        } else if(logSuccessMessages) {
            this.logger.info(message);
        }
    }
}